import React from "react";

import { useTranslation } from "react-i18next";

import {
	SelectableWallets,
	addCanBeSelected,
} from "@helpers/shared/addPropertyToWallet";
import { useRootAppStore } from "@hooks";
import { redirect } from "@state";
import { useAppContext } from "@state/hooks/shared";
import { getWalletTranslationKey } from "@utils";
import classNames from "classnames";
import { observer } from "mobx-react";

export default observer(function UserBalanceDisplay(props: {
	isSlim?: boolean;
	isInMenu?: boolean;
}) {
	const { t } = useTranslation("account-settings-navigation");
	const rootStore = useRootAppStore();
	const casinoSearchStore = rootStore.casinoSearchStore;
	const { culture } = useAppContext();

	if (rootStore.userAuthStore.userAccounts == null) {
		return null;
	}
	const shouldShowAccountSelector = true;

	const handleClick = () => {
		if (
			casinoSearchStore &&
			casinoSearchStore.casinoStore &&
			window.location.pathname.includes("games")
		) {
			casinoSearchStore.casinoStore.openTransferFundsMenu();
		} else {
			redirect(`/${culture}/account-settings`);
		}
	};

	const userWalletsMapped = addCanBeSelected(rootStore.userAuthStore);

	if (props.isSlim) {
		const wallet: SelectableWallets | null =
			userWalletsMapped.find(
				(account) =>
					account.id === rootStore.userAuthStore.activeWalletId
			) || userWalletsMapped[0];

		return (
			<button
				key={wallet?.abrv}
				className={classNames("btn--split__label btn--reset", {
					"is-single": userWalletsMapped.length <= 1,
					"is-multi": userWalletsMapped.length > 1,
				})}
				onClick={handleClick}
				style={{ cursor: "pointer" }}
			>
				<span className="btn--split__clip">
					{wallet?.balance.toFixed(2) +
						" " +
						wallet?.currency.displayCode}
				</span>
			</button>
		);
	}

	if (userWalletsMapped.length == 1) {
		return (
			<li>
				<label
					htmlFor={`${userWalletsMapped[0].id}${
						props.isInMenu ? "-menu" : ""
					}`}
					className="sidebar__balance__label"
				></label>
				<span className="sidebar__balance__type u-mar--left--reset">
					{t<string>(`account-settings-navigation:ACCOUNT_BALANCE`)}:
				</span>
				<span className="sidebar__balance__amount">
					<span className="sidebar__balance__value">
						{userWalletsMapped[0].balance.toFixed(2)}
					</span>
					<span className="sidebar__balance__currency">
						{userWalletsMapped[0].currency.displayCode}
					</span>
				</span>
			</li>
		);
	}

	return (
		<>
			{userWalletsMapped.map((ua) => {
				const walletName = t<string>(getWalletTranslationKey(ua.abrv));

				const itemClasses = classNames(
					"wallet__item input--radio input--radio--sml",
					{
						[`wallet__item--${ua.abrv}`]: shouldShowAccountSelector,
					}
				);

				const labelClasses = classNames(
					"wallet__account input--radio__label input--radio--sml__label",
					[`wallet__account--${ua.abrv}`],
					{
						"wallet__account--col": props.isInMenu,
					}
				);

				const iconClasses = classNames([
					`wallet__icon u-icon u-icon--sml u-icon--wallet u-icon--wallet--${ua.abrv}`,
				]);

				const balanceClasses = classNames("wallet__balance", {
					"wallet__balance--menu wallet__balance--full":
						props.isInMenu,
				});

				return (
					<li key={ua.abrv} className={itemClasses}>
						<AccountTypeSelector
							userAccount={ua}
							isInMenu={props.isInMenu}
							shouldShowAccountSelector={
								shouldShowAccountSelector
							}
						/>
						<label
							htmlFor={`${ua.id}${props.isInMenu ? "-menu" : ""}`}
							className={labelClasses}
						>
							<span className="wallet__type">
								<i className={iconClasses} />
								<span className="wallet__name">
									{walletName}:
								</span>
							</span>
							<span className={balanceClasses}>
								<span className="wallet__amount">
									{ua.balance.toFixed(2)}
								</span>
								<span className="wallet__currency">
									{ua.currency.displayCode}
								</span>
							</span>
						</label>
					</li>
				);
			})}
		</>
	);
});

const AccountTypeSelector = observer(function AccountTypeSelector(props: {
	userAccount: SelectableWallets;
	shouldShowAccountSelector?: boolean;
	isInMenu?: boolean;
}) {
	const rootStore = useRootAppStore();

	if (
		("canBeSelected" in props.userAccount &&
			props.userAccount.canBeSelected == false) ||
		!props.shouldShowAccountSelector
	) {
		return (
			<input
				id={`${props.userAccount.id}${props.isInMenu ? "-menu" : ""}`}
				name={`accountSelector${props.isInMenu ? "-menu" : ""}`}
				className="input--radio__box input--radio--sml__box is-hidden"
				type="radio"
				disabled={true}
			/>
		);
	}

	return (
		<input
			id={`${props.userAccount.id}${props.isInMenu ? "-menu" : ""}`}
			name={`accountSelector${props.isInMenu ? "-menu" : ""}`}
			className="input--radio__box input--radio--sml__box"
			type="radio"
			value={props.userAccount.id}
			onChange={() => {
				rootStore.userAuthStore.setActiveWalletId(props.userAccount.id);
                rootStore.betSlipStore.fetchFreeBets(true);

				if (
					props.userAccount.abrv ==
					"sport-betting-account-shop-online"
				) {
					rootStore.waitingPeriodStore.hidePopup();
				}
			}}
			checked={
				rootStore.userAuthStore.activeWalletId === props.userAccount.id
			}
		/>
	);
});
